/**
 * Phase 11 Beat 3 — Portal Change Request form (candidate side).
 * Categories: skills, salary, LinkedIn, resume, address, visa, availability,
 * portfolio, preferred_location (Phase 13 Beat 2 matching-engine fields).
 * Renders a compact side-panel form inside the Candidate Portal.
 */
import { useState } from "react";
import { toast } from "sonner";
import { PaperPlaneTilt, PencilSimple } from "@phosphor-icons/react";

const CATEGORIES = [
  { key: "skills", label: "Skills", fields: [{ path: "skills", label: "Skills (comma-separated)", input: "csv" }] },
  { key: "salary", label: "Salary Expectation", fields: [{ path: "salary_expectation", label: "New salary expectation", placeholder: "$140k – $160k" }] },
  { key: "availability", label: "Availability / Notice Period", fields: [{ path: "notice_period", label: "New notice period", placeholder: "2 weeks" }] },
  { key: "linkedin", label: "LinkedIn URL", fields: [{ path: "linkedin_url", label: "LinkedIn URL", placeholder: "https://linkedin.com/in/…" }] },
  { key: "portfolio", label: "Portfolio URL", fields: [{ path: "portfolio_url", label: "Portfolio / GitHub URL", placeholder: "https://…" }] },
  { key: "resume", label: "Resume Link", fields: [{ path: "resume_url", label: "Resume URL", placeholder: "https://…" }] },
  { key: "address", label: "Address", fields: [
    { path: "dossier.personal_info.street_address", label: "Street / Suite" },
    { path: "dossier.personal_info.current_city", label: "City" },
    { path: "dossier.personal_info.current_state", label: "State (2-letter)" },
    { path: "dossier.personal_info.zip_code", label: "Zip Code" },
  ] },
  { key: "preferred_location", label: "Location & Work Preferences", fields: [
    { path: "dossier.personal_info.preferred_cities", label: "Preferred cities (comma-separated)", input: "csv", placeholder: "Austin, Dallas, Houston" },
    { path: "dossier.personal_info.remote_ok", label: "Open to remote work?", input: "bool" },
    { path: "dossier.personal_info.hybrid_ok", label: "Open to hybrid work?", input: "bool" },
    { path: "dossier.personal_info.onsite_ok", label: "Open to on-site work?", input: "bool" },
    { path: "dossier.personal_info.willing_to_relocate", label: "Willing to relocate?", input: "bool" },
    { path: "dossier.personal_info.relocation_locations", label: "Relocation locations (comma-separated)", input: "csv", placeholder: "Only if willing to relocate" },
  ] },
  { key: "visa", label: "Visa Information", fields: [
    { path: "dossier.visa_info.visa_type", label: "Visa Type" },
    { path: "dossier.visa_info.visa_start_date", label: "Visa Start Date", type: "date" },
    { path: "dossier.visa_info.visa_end_date", label: "Visa End Date", type: "date" },
  ] },
];

export default function PortalChangeRequestForm({ authFetch, me, onSubmitted }) {
  const [openCat, setOpenCat] = useState(null);
  const [values, setValues] = useState({});
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const cat = CATEGORIES.find((c) => c.key === openCat);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fields = {};
      for (const f of cat.fields) {
        const v = values[f.path];
        if (v == null || v === "") continue;
        if (f.input === "csv") {
          fields[f.path] = v.split(",").map((s) => s.trim()).filter(Boolean);
        } else if (f.input === "bool") {
          fields[f.path] = v === "true" || v === true;
        } else {
          fields[f.path] = v;
        }
      }
      if (Object.keys(fields).length === 0) {
        toast.error("Enter at least one changed value.");
        return;
      }
      const r = await authFetch("/api/portal/change-requests", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ category: cat.key, fields, candidate_notes: notes }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Submission failed");
      toast.success("Change request submitted — your Group Lead will review shortly.");
      setOpenCat(null); setValues({}); setNotes("");
      onSubmitted && onSubmitted();
    } catch (ex) { toast.error(ex.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="portal-cr-form">
      <div>
        <h3 className="text-base font-semibold text-slate-900">Request a Profile Update</h3>
        <p className="text-sm text-slate-500 mt-1">Choose a category, tell us what changed, and your Group Lead will review + approve it.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {CATEGORIES.map((c) => (
          <button key={c.key}
                  onClick={() => { setOpenCat(c.key); setValues({}); setNotes(""); }}
                  data-testid={`cr-cat-${c.key}`}
                  className={`text-left border rounded-xl px-3 py-2.5 text-sm transition
                              ${openCat === c.key ? "border-sky-500 bg-sky-50 text-sky-800" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"}`}>
            <div className="flex items-center gap-2">
              <PencilSimple size={14} />
              <span className="font-medium">{c.label}</span>
            </div>
          </button>
        ))}
      </div>

      {cat && (
        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3" data-testid={`cr-form-${cat.key}`}>
          <div className="text-sm font-semibold text-slate-900">New values for {cat.label}</div>
          {cat.fields.map((f) => (
            <label key={f.path} className="block">
              <div className="text-xs font-medium text-slate-600 mb-1">{f.label}</div>
              {f.input === "bool" ? (
                <select value={values[f.path] ?? ""}
                        onChange={(e) => setValues((v) => ({ ...v, [f.path]: e.target.value }))}
                        data-testid={`cr-field-${f.path.replace(/\./g, "-")}`}
                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
                  <option value="">— Select —</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              ) : (
                <input type={f.type || "text"} value={values[f.path] || ""}
                       onChange={(e) => setValues((v) => ({ ...v, [f.path]: e.target.value }))}
                       placeholder={f.placeholder || ""}
                       data-testid={`cr-field-${f.path.replace(/\./g, "-")}`}
                       className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500" />
              )}
            </label>
          ))}
          <label className="block">
            <div className="text-xs font-medium text-slate-600 mb-1">Note to your Group Lead (optional)</div>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                      data-testid="cr-notes"
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500 resize-none"
                      placeholder="Why did this change? (helpful for the reviewer)" />
          </label>
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
            <button type="button" onClick={() => { setOpenCat(null); setValues({}); setNotes(""); }}
                    data-testid="cr-cancel"
                    className="text-sm border border-slate-200 rounded-lg px-3 py-2 hover:bg-slate-50">
              Cancel
            </button>
            <button type="submit" disabled={busy}
                    data-testid="cr-submit"
                    className="text-sm bg-sky-600 hover:bg-sky-700 text-white rounded-lg px-4 py-2 inline-flex items-center gap-1.5 font-medium disabled:opacity-40">
              <PaperPlaneTilt size={14} /> Submit for Review
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
